import type { CityAttractionGroup } from '../types';
import {
  Attraction,
  CityPass,
  Country,
  DirectoryCountryRow,
  DirectorySearchHit,
  FoodInfo,
  TravelInfo,
  VisaInfo,
} from '../types';
import { SITE_DATA } from '../data/siteData';
import { toCitySlug } from '../utils/citySlug';

interface RawCountry {
  id: string;
  name: string;
  name_en: string;
  flag: string;
  continent: string;
  popularity: number;
  description?: string;
  description_en?: string;
}

interface RawVisa {
  country_id: string;
  type: string;
  schengen: number | boolean;
  description?: string;
  official_url?: string;
}

interface RawAttraction {
  id: string;
  cityname: string;
  cityname_en?: string;
  attractions_name: string;
  attractions_name_en?: string;
  description?: string;
  description_en?: string;
  official_link?: string;
  official_link_en?: string;
  price_range?: string;
  price_range_en?: string;
  currency?: string;
}

interface RawCityPass {
  country_id: string;
  city_name: string;
  city_name_en?: string;
  transportation?: string;
  transportation_en?: string;
  recommendation?: string;
  recommendation_en?: string;
  buy_ticket?: string;
  buy_ticket_en?: string;
}

interface RawTravelInfo {
  country_id: string;
  peak_season?: string;
  peak_season_en?: string;
  off_season?: string;
  off_season_en?: string;
  best_months?: string;
  best_months_en?: string;
  recent_notices?: string;
  recent_notices_en?: string;
  climate_info?: string;
  climate_info_en?: string;
  'Currency Code'?: string;
  Language?: string;
  'Time Zone (GMT Offset)'?: string;
}

interface RawFood {
  country_id: string;
  food?: string;
  food_en?: string;
  price_per_meal?: string;
  price_per_meal_en?: string;
  tips?: string;
  tips_en?: string;
  notification?: string;
  notification_en?: string;
  find_local_food?: string;
  find_local_food_en?: string;
}

interface DataBundle {
  countries: RawCountry[];
  visaByCountry: Map<string, RawVisa>;
  attractionsByCountry: Map<string, RawAttraction[]>;
  cityPassesByCountry: Map<string, RawCityPass[]>;
  travelByCountry: Map<string, RawTravelInfo>;
  foodByCountry: Map<string, RawFood>;
}

const pickLocalized = (zh?: string, en?: string, isEn = false): string => {
  const z = (zh || '').trim();
  const e = (en || '').trim();
  if (isEn) return e || z;
  return z || e;
};

const isUrl = (value?: string): boolean => {
  if (!value) return false;
  const t = value.trim().toLowerCase();
  return t.startsWith('http://') || t.startsWith('https://');
};

const hasCjk = (value: string): boolean =>
  /[\u4e00-\u9fff\u3000-\u303f]/.test(value);

const officialLinkDisplay = (att: RawAttraction, isEn: boolean): string | undefined => {
  const raw = att.official_link || '';
  if (isUrl(raw)) return raw;
  const display = pickLocalized(att.official_link, att.official_link_en, isEn);
  if (isEn && hasCjk(display)) return 'No official ticket link';
  return display || undefined;
};

const safeJsonList = (value?: string): string[] => {
  if (!value?.trim()) return [];
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean);
    if (typeof parsed === 'string' && parsed.trim()) return [parsed.trim()];
  } catch {
    // plain text
  }
  return [value.trim()];
};

const parseBestMonths = (value?: string, isEn = false): string[] => {
  if (!value?.trim()) return [];
  if (!isEn) return safeJsonList(value);
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean);
  } catch {
    // fall through
  }
  return value
    .replace(/，|、|；|;/g, ',')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

const groupByCountryId = <T extends { country_id: string }>(rows: T[]): Map<string, T[]> => {
  const map = new Map<string, T[]>();
  for (const row of rows) {
    const list = map.get(row.country_id) || [];
    list.push(row);
    map.set(row.country_id, list);
  }
  return map;
};

const groupAttractions = (rows: RawAttraction[]): Map<string, RawAttraction[]> => {
  const map = new Map<string, RawAttraction[]>();
  for (const row of rows) {
    const list = map.get(row.id) || [];
    list.push(row);
    map.set(row.id, list);
  }
  return map;
};

let bundle: DataBundle | null = null;

function getBundle(): DataBundle {
  if (bundle) return bundle;

  const countries = SITE_DATA.countries as RawCountry[];
  const visas = SITE_DATA.visas as RawVisa[];
  const attractions = SITE_DATA.attractions as RawAttraction[];
  const cityPasses = SITE_DATA.cityPasses as RawCityPass[];
  const travelInfo = SITE_DATA.travelInfo as RawTravelInfo[];
  const food = SITE_DATA.food as RawFood[];

  bundle = {
    countries,
    visaByCountry: new Map(visas.map((v) => [v.country_id, v])),
    attractionsByCountry: groupAttractions(attractions),
    cityPassesByCountry: groupByCountryId(cityPasses),
    travelByCountry: new Map(travelInfo.map((t) => [t.country_id, t])),
    foodByCountry: new Map(food.map((f) => [f.country_id, f])),
  };
  return bundle;
}

const buildVisaInfo = (visa: RawVisa | undefined, isEn: boolean): VisaInfo | undefined => {
  if (!visa || isEn) return undefined;
  return {
    type: visa.type,
    description: visa.description || '',
    officialUrl: visa.official_url && visa.official_url !== '/' ? visa.official_url : undefined,
    warnings: [],
    schengen: Boolean(visa.schengen),
  };
};

const buildListVisaBlock = (visa: RawVisa | undefined, isEn: boolean): VisaInfo | undefined => {
  if (!visa) return undefined;
  return {
    type: visa.type,
    description: visa.description || '',
    schengen: Boolean(visa.schengen),
    officialUrl: isEn ? undefined : visa.official_url && visa.official_url !== '/' ? visa.official_url : undefined,
  };
};

const buildAttraction = (att: RawAttraction, index: number, isEn: boolean): Attraction => ({
  id: index + 1,
  cityname: pickLocalized(att.cityname, att.cityname_en, isEn),
  citynameEn: att.cityname_en,
  attractionsName: pickLocalized(att.attractions_name, att.attractions_name_en, isEn),
  attractionsNameZh: att.attractions_name,
  attractionsNameEn: att.attractions_name_en,
  officialLink: officialLinkDisplay(att, isEn),
  description: pickLocalized(att.description, att.description_en, isEn),
  descriptionEn: att.description_en,
  priceRange: pickLocalized(att.price_range, att.price_range_en, isEn),
  currency: att.currency,
});

const buildTravelInfoForList = (ti: RawTravelInfo | undefined, isEn: boolean): TravelInfo | undefined => {
  if (!ti) return undefined;
  const bestMonthsRaw = pickLocalized(ti.best_months, ti.best_months_en, isEn);
  return {
    bestMonths: parseBestMonths(bestMonthsRaw, isEn),
    climateInfo: pickLocalized(ti.climate_info, ti.climate_info_en, isEn),
  };
};

const buildTravelInfoForDetail = (ti: RawTravelInfo | undefined, isEn: boolean): TravelInfo | undefined => {
  if (!ti) return undefined;
  const bestMonthsRaw = pickLocalized(ti.best_months, ti.best_months_en, isEn);
  const recentRaw = pickLocalized(ti.recent_notices, ti.recent_notices_en, isEn);
  return {
    peakSeason: pickLocalized(ti.peak_season, ti.peak_season_en, isEn),
    peakSeasonEn: ti.peak_season_en,
    offSeason: pickLocalized(ti.off_season, ti.off_season_en, isEn),
    bestMonths: parseBestMonths(bestMonthsRaw, isEn),
    bestMonthsEn: ti.best_months_en ? parseBestMonths(ti.best_months_en, true) : undefined,
    recentNotices: safeJsonList(recentRaw),
    recentNoticesEn: ti.recent_notices_en ? safeJsonList(ti.recent_notices_en) : undefined,
    climateInfo: pickLocalized(ti.climate_info, ti.climate_info_en, isEn),
    climateInfoEn: ti.climate_info_en,
    currency: ti['Currency Code'],
    languages: ti.Language ? [ti.Language] : [],
    timezone: ti['Time Zone (GMT Offset)'],
  };
};

const buildFoodInfo = (food: RawFood | undefined, isEn: boolean): FoodInfo | undefined => {
  if (!food) return undefined;
  return {
    id: 1,
    countryId: food.country_id,
    food: pickLocalized(food.food, food.food_en, isEn),
    pricePerMeal: pickLocalized(food.price_per_meal, food.price_per_meal_en, isEn),
    tips: pickLocalized(food.tips, food.tips_en, isEn),
    notification: pickLocalized(food.notification, food.notification_en, isEn),
    findLocalFood: pickLocalized(food.find_local_food, food.find_local_food_en, isEn),
  };
};

const buildCityPass = (cp: RawCityPass, index: number, isEn: boolean): CityPass => ({
  id: index + 1,
  cityName: pickLocalized(cp.city_name, cp.city_name_en, isEn),
  cityNameEn: cp.city_name_en,
  transportation: pickLocalized(cp.transportation, cp.transportation_en, isEn),
  recommendation: pickLocalized(cp.recommendation, cp.recommendation_en, isEn),
  buyTicket: pickLocalized(cp.buy_ticket, cp.buy_ticket_en, isEn),
});

const buildCountrySummary = (
  country: RawCountry,
  data: DataBundle,
  isEn: boolean
): Country => {
  const visa = data.visaByCountry.get(country.id);
  const attractions = (data.attractionsByCountry.get(country.id) || []).slice(0, 3);
  const travel = data.travelByCountry.get(country.id);

  return {
    id: country.id,
    name: country.name,
    nameEn: country.name_en,
    flag: country.flag,
    popularity: country.popularity,
    continent: country.continent,
    visa: buildListVisaBlock(visa, isEn),
    attractions: attractions.map((att, idx) => buildAttraction(att, idx, isEn)),
    travelInfo: buildTravelInfoForList(travel, isEn),
  };
};

const buildCountryDetail = (
  country: RawCountry,
  data: DataBundle,
  isEn: boolean
): Country => {
  const visa = data.visaByCountry.get(country.id);
  const attractions = data.attractionsByCountry.get(country.id) || [];
  const cityPasses = data.cityPassesByCountry.get(country.id) || [];
  const travel = data.travelByCountry.get(country.id);
  const food = data.foodByCountry.get(country.id);

  return {
    id: country.id,
    name: country.name,
    nameEn: country.name_en,
    flag: country.flag,
    popularity: country.popularity,
    continent: country.continent,
    visa: buildVisaInfo(visa, isEn),
    attractions: attractions.map((att, idx) => buildAttraction(att, idx, isEn)),
    cityPasses: cityPasses.map((cp, idx) => buildCityPass(cp, idx, isEn)),
    regionalPasses: [],
    travelInfo: buildTravelInfoForDetail(travel, isEn),
    foodInfo: buildFoodInfo(food, isEn),
  };
};

export function getAllCountries(continent?: string, locale = 'zh'): Country[] {
  const data = getBundle();
  const isEn = locale.toLowerCase() === 'en';
  let rows = data.countries;

  if (continent) {
    rows = rows.filter((country) => country.continent === continent);
  }

  return rows
    .map((country) => buildCountrySummary(country, data, isEn))
    .sort((a, b) => a.popularity - b.popularity);
}

export function getCountryById(id: string, locale = 'zh'): Country | null {
  const data = getBundle();
  const country = data.countries.find((row) => row.id === id);
  if (!country) return null;
  const isEn = locale.toLowerCase() === 'en';
  return buildCountryDetail(country, data, isEn);
}

export function searchCountries(query: string, locale = 'zh'): Country[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const all = getAllCountries(undefined, locale);
  return all.filter(
    (country) =>
      country.name.toLowerCase().includes(q) ||
      country.nameEn.toLowerCase().includes(q)
  );
}

export function getPopularCountries(limit = 10, locale = 'zh'): Country[] {
  const all = getAllCountries(undefined, locale);
  return all.slice(0, limit);
}

function buildDirectoryCountryRow(
  country: RawCountry,
  data: DataBundle,
  isEn: boolean
): DirectoryCountryRow {
  const rawAttractions = data.attractionsByCountry.get(country.id) || [];
  const cityMap = new Map<
    string,
    { slug: string; name: string; nameEn: string; count: number }
  >();

  for (const att of rawAttractions) {
    const slug = toCitySlug(att.cityname_en, att.cityname);
    const existing = cityMap.get(slug);
    if (existing) {
      existing.count += 1;
    } else {
      cityMap.set(slug, {
        slug,
        name: att.cityname,
        nameEn: att.cityname_en || att.cityname,
        count: 1,
      });
    }
  }

  const cities = Array.from(cityMap.values())
    .map((c) => ({
      slug: c.slug,
      name: isEn ? pickLocalized(c.name, c.nameEn, true) : c.name,
      nameEn: c.nameEn,
      attractionCount: c.count,
    }))
    .sort((a, b) => b.attractionCount - a.attractionCount);

  return {
    id: country.id,
    name: country.name,
    nameEn: country.name_en,
    flag: country.flag,
    popularity: country.popularity,
    attractionCount: rawAttractions.length,
    cities,
  };
}

export function getDirectoryCountries(
  locale = 'zh',
  continent?: string
): DirectoryCountryRow[] {
  const data = getBundle();
  const isEn = locale.toLowerCase() === 'en';
  let rows = data.countries;
  if (continent) {
    rows = rows.filter((c) => c.continent === continent);
  }
  return rows
    .map((c) => buildDirectoryCountryRow(c, data, isEn))
    .filter((c) => c.attractionCount > 0)
    .sort((a, b) => a.popularity - b.popularity);
}

export function getDirectoryCountry(
  countryId: string,
  locale = 'zh'
): DirectoryCountryRow | null {
  const data = getBundle();
  const country = data.countries.find((c) => c.id === countryId);
  if (!country) return null;
  const isEn = locale.toLowerCase() === 'en';
  return buildDirectoryCountryRow(country, data, isEn);
}

export function getCountryCityGroups(
  countryId: string,
  locale = 'zh'
): CityAttractionGroup[] | null {
  const data = getBundle();
  const country = data.countries.find((c) => c.id === countryId);
  if (!country) return null;

  const isEn = locale.toLowerCase() === 'en';
  const rawAttractions = data.attractionsByCountry.get(countryId) || [];
  const groups = new Map<string, CityAttractionGroup>();

  rawAttractions.forEach((att, index) => {
    const slug = toCitySlug(att.cityname_en, att.cityname);
    let group = groups.get(slug);
    if (!group) {
      group = {
        slug,
        cityDisplayName: isEn
          ? pickLocalized(att.cityname, att.cityname_en, true)
          : att.cityname,
        attractions: [],
      };
      groups.set(slug, group);
    }
    group.attractions.push(buildAttraction(att, index, isEn));
  });

  Array.from(groups.values()).forEach((group) => {
    group.attractions.sort((a, b) =>
      a.attractionsName.localeCompare(b.attractionsName, isEn ? 'en' : 'zh')
    );
  });

  return Array.from(groups.values()).sort(
    (a, b) => b.attractions.length - a.attractions.length
  );
}

export function getCityAttractions(
  countryId: string,
  citySlug: string,
  locale = 'zh'
): { country: Country; cityDisplayName: string; attractions: Attraction[] } | null {
  const country = getCountryById(countryId, locale);
  if (!country) return null;

  const normalized = citySlug.trim().toLowerCase();
  const groups = getCountryCityGroups(countryId, locale);
  const group = groups?.find((g) => g.slug === normalized);
  if (!group) return null;

  return {
    country,
    cityDisplayName: group.cityDisplayName,
    attractions: group.attractions,
  };
}

export function searchDirectory(
  query: string,
  locale = 'zh',
  limit = 12
): DirectorySearchHit[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];

  const isEn = locale.toLowerCase() === 'en';
  const data = getBundle();
  const hits: DirectorySearchHit[] = [];

  for (const country of data.countries) {
    const labelZh = country.name;
    const labelEn = country.name_en;
    if (
      labelZh.toLowerCase().includes(q) ||
      labelEn.toLowerCase().includes(q)
    ) {
      hits.push({
        kind: 'country',
        countryId: country.id,
        countryLabel: isEn ? labelEn : labelZh,
      });
    }
  }

  for (const country of data.countries) {
    const attractions = data.attractionsByCountry.get(country.id) || [];
    const countryLabel = isEn ? country.name_en : country.name;
    const seenCities = new Set<string>();

    for (const att of attractions) {
      const citySlug = toCitySlug(att.cityname_en, att.cityname);
      const cityLabel = isEn
        ? pickLocalized(att.cityname, att.cityname_en, true)
        : att.cityname;
      const cityKey = `${country.id}:${citySlug}`;
      if (
        !seenCities.has(cityKey) &&
        (cityLabel.toLowerCase().includes(q) ||
          (att.cityname_en || '').toLowerCase().includes(q) ||
          att.cityname.toLowerCase().includes(q))
      ) {
        seenCities.add(cityKey);
        hits.push({
          kind: 'city',
          countryId: country.id,
          countryLabel,
          citySlug,
          cityLabel,
        });
      }

      const attLabel = isEn
        ? pickLocalized(att.attractions_name, att.attractions_name_en, true)
        : att.attractions_name;
      const attEn = (att.attractions_name_en || '').toLowerCase();
      if (
        attLabel.toLowerCase().includes(q) ||
        att.attractions_name.toLowerCase().includes(q) ||
        attEn.includes(q)
      ) {
        hits.push({
          kind: 'attraction',
          countryId: country.id,
          countryLabel,
          citySlug,
          cityLabel,
          attractionLabel: attLabel,
          officialLink: officialLinkDisplay(att, isEn),
        });
      }
    }
  }

  const dedup = new Map<string, DirectorySearchHit>();
  for (const hit of hits) {
    const key =
      hit.kind === 'country'
        ? `c:${hit.countryId}`
        : hit.kind === 'city'
          ? `city:${hit.countryId}:${hit.citySlug}`
          : `a:${hit.countryId}:${hit.citySlug}:${hit.attractionLabel}`;
    if (!dedup.has(key)) dedup.set(key, hit);
  }

  return Array.from(dedup.values()).slice(0, limit);
}
